// Centralized runtime configuration for API and WebSocket base URLs
// Works for web (dev/prod) and native (Capacitor) environments

import { Capacitor } from '@capacitor/core';

const isNative = () => {
  try {
    return Capacitor?.isNativePlatform?.() === true;
  } catch (_) {
    return false;
  }
};

// Optionally allow overriding at runtime via global variable for QA builds
const getRuntimeOverride = (key) => {
  try {
    if (typeof window !== 'undefined' && window.__OKIO_CONFIG__ && window.__OKIO_CONFIG__[key]) {
      return window.__OKIO_CONFIG__[key];
    }
  } catch (_) {}
  return null;
};

function withApiSuffix(base) {
  if (!base) return '/api';
  try {
    if (base.startsWith('http')) {
      const u = new URL(base);
      const normalized = (u.pathname || '/').replace(/\/+$/, '');
      if (normalized === '' || normalized === '/') return `${u.origin}/api`;
      if (normalized === '/api' || normalized.endsWith('/api')) return `${u.origin}${normalized}`;
      return `${u.origin}${normalized}/api`;
    }
  } catch (_) {}
  // Relative base
  const rel = base.startsWith('/') ? base : `/${base}`;
  const normalized = rel.replace(/\/+$/, '');
  if (normalized === '/api' || normalized.endsWith('/api')) return normalized;
  return `${normalized}/api`;
}

function deriveWsBaseFromApi(apiBase) {
  try {
    if (apiBase.startsWith('http')) {
      const u = new URL(apiBase);
      const isWss = u.protocol === 'https:';
      // ensure path ends with /api
      const apiPath = u.pathname.endsWith('/api') ? u.pathname : (u.pathname.replace(/\/$/, '') + '/api');
      return `${isWss ? 'wss:' : 'ws:'}//${u.host}${apiPath}/ws`;
    }
  } catch (_) {}
  // Relative
  const rel = apiBase.startsWith('/') ? apiBase : `/${apiBase}`;
  const apiPath = rel.endsWith('/api') ? rel : `${rel.replace(/\/$/, '')}/api`;
  return `${apiPath}/ws`;
}

export const getApiBaseUrl = () => {
  const override = getRuntimeOverride('API_BASE_URL');
  if (override) return override;

  const envMobile = process.env.REACT_APP_MOBILE_API_URL;
  const envWeb = process.env.REACT_APP_API_URL;

  if (isNative()) {
    // Prefer explicit mobile API URL when running as a native app
    if (envMobile) return withApiSuffix(envMobile);
    if (envWeb) return withApiSuffix(envWeb);
    // Fallback: try to use HTTPS production API by convention if provided via window
    // Otherwise, developers should set REACT_APP_MOBILE_API_URL
    return 'https://okio.ai/api';
  }

  // Web environments
  if (envWeb) return withApiSuffix(envWeb);
  if (process.env.NODE_ENV === 'development') return 'http://localhost:8000/api/';
  // In production web, assume the backend is reverse-proxied under /api
  return '/api';
};

export const getWsBaseUrl = () => {
  const override = getRuntimeOverride('WS_BASE_URL');
  if (override) return override;

  // Derive WS base from API base
  const apiBase = getApiBaseUrl();
  return deriveWsBaseFromApi(apiBase);
};

export const getPublicWebOrigin = () => {
  const override = getRuntimeOverride('PUBLIC_WEB_ORIGIN');
  if (override) {
    return String(override).replace(/\/+$/, '');
  }

  const apiBase = getApiBaseUrl().replace(/\/+$/, '');
  if (apiBase.startsWith('http')) {
    return apiBase.replace(/\/api$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }

  return 'https://okio.ai';
};

export const getAndroidPlayStoreUrl = (packageName = 'ai.okio.app') => {
  const override = getRuntimeOverride('ANDROID_PLAY_STORE_URL');
  if (override) {
    return String(override);
  }

  return `https://play.google.com/store/apps/details?id=${encodeURIComponent(packageName)}`;
};

export const getAndroidUpdateMode = () => {
  const override = getRuntimeOverride('ANDROID_UPDATE_MODE');
  const configured = (override || process.env.REACT_APP_ANDROID_UPDATE_MODE || 'flexible')
    .toString()
    .trim()
    .toLowerCase();

  return configured === 'immediate' ? 'immediate' : 'flexible';
};

export const platform = { isNative };

// Build absolute API URL for resources (works on web and native)
// Supports Cloudflare Images variants for optimized loading
export const getImageUrl = (pathOrId, variant = null) => {
  try {
    if (!pathOrId) return '';
    
    const value = String(pathOrId);

    // Support local object URLs / inline data URLs (optimistic previews, etc.)
    if (
      value.startsWith('blob:') ||
      value.startsWith('data:') ||
      value.startsWith('file:')
    ) {
      return value;
    }
    
    // If already absolute URL, check if it's a Cloudflare Images URL that needs variant
    if ((/^https?:\/\//i).test(value)) {
      // Check if it's a Cloudflare Images URL (imagedelivery.net)
      if (value.includes('imagedelivery.net') && variant) {
        // Replace the variant part of the URL
        // Format: https://imagedelivery.net/<account_hash>/<image_id>/<variant>
        const match = value.match(/^(https:\/\/imagedelivery\.net\/[^/]+\/[^/]+)\/[^/?]+(.*)$/);
        if (match) {
          return `${match[1]}/${variant}${match[2] || ''}`;
        }
      }
      return value;
    }

    const base = getApiBaseUrl().replace(/\/$/, ''); // ensure no trailing slash
    const baseOrigin = base.replace(/\/api$/, '');   // strip trailing /api to prepend raw paths

    // If caller passed a full API path like "/api/upload/image/abc"
    if (value.startsWith('/api/')) {
      return `${baseOrigin}${value}`;
    }

    // If caller passed a root path like "/upload/image/abc"
    if (value.startsWith('/upload/')) {
      return `${baseOrigin}${value}`;
    }

    // Otherwise treat as an image id/filename
    const id = value.replace(/^\/+/, '');
    return `${base}/upload/image/${id}`;
  } catch (_) {
    return '';
  }
};


